import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Mic, Volume2, CheckCircle2, Loader2, ChevronRight,
  AlertTriangle, Waves, Globe, Zap, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { saveVoiceCalibration } from '../services/api';

const CALIBRATION_PHRASES = [
  {
    id: 'warmup',
    label: 'Warm-up',
    text: 'Hello, my name is a candidate for this position. I am excited to be here for this interview today.',
    purpose: 'Basic voice capture and volume calibration'
  },
  {
    id: 'technical',
    label: 'Technical',
    text: 'I have experience working with databases, application programming interfaces, and cloud-based infrastructure solutions.',
    purpose: 'Technical vocabulary and pronunciation patterns'
  },
  {
    id: 'complex',
    label: 'Fluency',
    text: 'Throughout my career, I have collaborated with cross-functional teams to deliver projects on time while maintaining high quality standards.',
    purpose: 'Complex sentence structure and speech flow'
  }
];

const ACCENT_PROFILES = [
  { code: 'en-US', label: 'US English' },
  { code: 'en-GB', label: 'UK English' },
  { code: 'en-IN', label: 'Indian English' },
  { code: 'en-AU', label: 'Australian English' },
  { code: 'en-PH', label: 'Philippine English' },
  { code: 'en-SG', label: 'Singapore English' },
  { code: 'en-ZA', label: 'South African English' },
  { code: 'en-NZ', label: 'New Zealand English' },
];

const getSR = () => {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  return SR ? new SR() : null;
};

const wordSimilarity = (recognized, expected) => {
  const rWords = recognized.toLowerCase().replace(/[^\w\s]/g, '').split(/\s+/).filter(Boolean);
  const eWords = expected.toLowerCase().replace(/[^\w\s]/g, '').split(/\s+/).filter(Boolean);
  if (eWords.length === 0) return 0;
  let matches = 0;
  for (const ew of eWords) {
    if (rWords.some(rw => rw === ew || (rw.length > 3 && ew.length > 3 && levenshtein(rw, ew) <= 2))) {
      matches++;
    }
  }
  return Math.round((matches / eWords.length) * 100);
};

const levenshtein = (a, b) => {
  const matrix = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      matrix[i][j] = a[i - 1] === b[j - 1]
        ? matrix[i - 1][j - 1]
        : 1 + Math.min(matrix[i - 1][j], matrix[i][j - 1], matrix[i - 1][j - 1]);
    }
  }
  return matrix[a.length][b.length];
};

const AudioMeter = ({ level, active }) => (
  <div className="flex items-center gap-1.5 h-8">
    {Array.from({ length: 20 }).map((_, i) => {
      const threshold = (i / 20) * 100;
      const isActive = active && level > threshold;
      const color = i < 6 ? 'bg-green-500' : i < 14 ? 'bg-emerald-400' : 'bg-amber-400';
      return (
        <div
          key={i}
          className={`w-1.5 rounded-full transition-all duration-75 ${isActive ? color : 'bg-muted/30'}`}
          style={{ height: `${12 + i * 1.2}px` }}
        />
      );
    })}
  </div>
);

export default function VoiceCalibration({ token, onComplete, mediaStream }) {
  const [phase, setPhase] = useState('intro'); // intro, recording, testing, results
  const [currentPhraseIdx, setCurrentPhraseIdx] = useState(0);
  const [phraseResults, setPhraseResults] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);
  const [accentResults, setAccentResults] = useState({});
  const [bestLang, setBestLang] = useState('en-US');
  const [clarityScore, setClarityScore] = useState(0);
  const [speechRate, setSpeechRate] = useState(0);
  const [noiseBaseline, setNoiseBaseline] = useState(0);
  const [saving, setSaving] = useState(false);
  const [testingAccents, setTestingAccents] = useState(false);

  const recognitionRef = useRef(null);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);
  const startTimeRef = useRef(null);
  const wordCountRef = useRef(0);
  const noiseReadingsRef = useRef([]);
  const isActiveRef = useRef(false);          // Track recording state for closures
  const fullTextRef = useRef('');              // Accumulated recognized text
  const silenceTimerRef = useRef(null);        // Auto-stop on silence
  const lastSpeechTimeRef = useRef(null);      // Track last speech for silence detection

  // Audio level monitoring
  useEffect(() => {
    if (!mediaStream) return;
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(mediaStream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = { analyser, audioCtx };

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const update = () => {
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        const normalized = Math.min(100, (avg / 128) * 100);
        setAudioLevel(normalized);
        noiseReadingsRef.current.push(normalized);
        animFrameRef.current = requestAnimationFrame(update);
      };
      update();
    } catch (e) { /* ignore */ }

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (analyserRef.current?.audioCtx) {
        try { analyserRef.current.audioCtx.close(); } catch (e) {}
      }
    };
  }, [mediaStream]);

  // Capture noise baseline during intro
  useEffect(() => {
    if (phase === 'intro') {
      const timer = setTimeout(() => {
        const readings = noiseReadingsRef.current;
        if (readings.length > 0) {
          const baseline = readings.reduce((a, b) => a + b, 0) / readings.length;
          setNoiseBaseline(Math.round(baseline * 10) / 10);
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [phase]);

  const finishPhrase = useCallback((text) => {
    const phrase = CALIBRATION_PHRASES[currentPhraseIdx];
    if (!phrase) return;

    const elapsed = (Date.now() - (startTimeRef.current || Date.now())) / 1000;
    const words = (text || '').split(/\s+/).filter(Boolean).length;
    const wpm = elapsed > 1 ? Math.round((words / elapsed) * 60) : 0;
    const accuracy = wordSimilarity(text || '', phrase.text);

    setPhraseResults(prev => [...prev, {
      phraseId: phrase.id,
      recognized: text || '',
      expected: phrase.text,
      accuracy,
      wpm,
      duration: Math.round(elapsed)
    }]);

    setIsListening(false);
    isActiveRef.current = false;
    fullTextRef.current = '';

    // Clear timers
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }

    // Move to next phrase or accent testing
    if (currentPhraseIdx < CALIBRATION_PHRASES.length - 1) {
      setCurrentPhraseIdx(prev => prev + 1);
      setLiveTranscript('');
    } else {
      runAccentTest(text);
    }
  }, [currentPhraseIdx]);

  const startPhraseRecording = useCallback(() => {
    const phrase = CALIBRATION_PHRASES[currentPhraseIdx];
    if (!phrase) return;

    // Reset state
    isActiveRef.current = true;
    fullTextRef.current = '';
    setIsListening(true);
    setLiveTranscript('');
    startTimeRef.current = Date.now();
    lastSpeechTimeRef.current = Date.now();
    wordCountRef.current = 0;

    const recognition = getSR();
    if (!recognition) {
      toast.error('Speech recognition not available in this browser');
      setIsListening(false);
      isActiveRef.current = false;
      return;
    }

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = bestLang || 'en-US';

    recognition.onresult = (event) => {
      let interim = '', final = '';
      for (let i = 0; i < event.results.length; i++) {
        if (event.results[i].isFinal) final += event.results[i][0].transcript + ' ';
        else interim += event.results[i][0].transcript;
      }
      if (final) fullTextRef.current = final.trim();
      const display = fullTextRef.current || interim;
      setLiveTranscript(display);
      wordCountRef.current = display.split(/\s+/).filter(Boolean).length;
      lastSpeechTimeRef.current = Date.now();

      // Reset silence timer on every speech event
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = setTimeout(() => {
        // 3 seconds of silence after speech = auto-finish
        if (isActiveRef.current && fullTextRef.current.length > 5) {
          try { recognition.stop(); } catch (e) {}
        }
      }, 3000);
    };

    recognition.onerror = (e) => {
      if (e.error === 'no-speech') {
        // No speech detected — just keep listening, don't crash
        return;
      }
      if (e.error !== 'aborted') {
        console.warn('Calibration speech error:', e.error);
      }
    };

    recognition.onend = () => {
      // Always process results when recognition ends (whether manual stop or auto)
      if (isActiveRef.current) {
        finishPhrase(fullTextRef.current);
      }
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
    } catch (e) {
      setIsListening(false);
      isActiveRef.current = false;
      toast.error('Could not start voice capture');
    }

    // Safety auto-stop after 15 seconds max
    setTimeout(() => {
      if (isActiveRef.current) {
        try { recognitionRef.current?.stop(); } catch (e) {}
      }
    }, 15000);
  }, [currentPhraseIdx, bestLang, finishPhrase]);

  const stopRecording = useCallback(() => {
    // Just stop the recognition — onend will handle the result processing
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) {}
    }
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }

    // Fallback: if onend doesn't fire within 500ms, process manually
    setTimeout(() => {
      if (isActiveRef.current) {
        finishPhrase(fullTextRef.current);
      }
    }, 500);
  }, [finishPhrase]);

  const runAccentTest = async (lastRecognizedText) => {
    setTestingAccents(true);
    setPhase('testing');

    const testText = CALIBRATION_PHRASES[2].text; // use the complex phrase
    const results = {};

    // Test each accent profile sequentially
    for (const profile of ACCENT_PROFILES) {
      try {
        const score = await testAccentProfile(profile.code, testText);
        results[profile.code] = score;
      } catch (e) {
        results[profile.code] = 0;
      }
    }

    setAccentResults(results);

    // Find best language
    let maxScore = 0;
    let maxLang = 'en-US';
    for (const [lang, score] of Object.entries(results)) {
      if (score > maxScore) {
        maxScore = score;
        maxLang = lang;
      }
    }

    setBestLang(maxLang);

    // Calculate overall clarity from phrase results
    const avgAccuracy = phraseResults.length > 0
      ? Math.round(phraseResults.reduce((a, b) => a + b.accuracy, 0) / phraseResults.length)
      : 0;
    setClarityScore(Math.max(avgAccuracy, maxScore));

    // Calculate average speech rate
    const avgWpm = phraseResults.length > 0
      ? Math.round(phraseResults.reduce((a, b) => a + b.wpm, 0) / phraseResults.length)
      : 0;
    setSpeechRate(avgWpm);

    setTestingAccents(false);
    setPhase('results');
  };

  const testAccentProfile = (langCode, expectedText) => {
    return new Promise((resolve) => {
      const recognition = getSR();
      if (!recognition) { resolve(0); return; }

      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = langCode;

      let result = '';

      recognition.onresult = (event) => {
        for (let i = 0; i < event.results.length; i++) {
          result += event.results[i][0].transcript;
        }
      };

      recognition.onerror = () => resolve(0);
      recognition.onend = () => {
        // Use last phrase results transcript instead of re-recording
        // Compare stored recognition against expected
        const lastPhrase = phraseResults[phraseResults.length - 1];
        if (lastPhrase) {
          resolve(lastPhrase.accuracy);
        } else {
          resolve(wordSimilarity(result, expectedText));
        }
      };

      // Brief test - just check if the language model initializes without error
      try {
        recognition.start();
        setTimeout(() => { try { recognition.stop(); } catch (e) {} }, 500);
      } catch (e) {
        resolve(0);
      }
    });
  };

  const handleSaveAndProceed = async () => {
    setSaving(true);
    try {
      const calibrationData = {
        best_language: bestLang,
        speech_rate_wpm: speechRate,
        clarity_score: clarityScore,
        volume_level: Math.round(audioLevel),
        noise_baseline: noiseBaseline,
        accent_scores: accentResults,
        calibration_text_accuracy: clarityScore
      };
      await saveVoiceCalibration(token, calibrationData);
      onComplete(calibrationData);
    } catch (e) {
      // Proceed anyway even if save fails
      onComplete({
        best_language: bestLang,
        speech_rate_wpm: speechRate,
        clarity_score: clarityScore
      });
    } finally {
      setSaving(false);
    }
  };

  const currentPhrase = CALIBRATION_PHRASES[currentPhraseIdx];

  // Intro screen
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="voice-calibration-intro">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-4">
              <Waves className="h-8 w-8 text-indigo-500" />
            </div>
            <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Voice Calibration
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Let's tune your microphone for the best experience. You'll read 3 short phrases
              so the system can adapt to your voice and accent.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
              <div className="flex items-center gap-3">
                <Mic className="h-5 w-5 text-indigo-500 shrink-0" />
                <div>
                  <p className="text-sm font-medium">Microphone Active</p>
                  <AudioMeter level={audioLevel} active={true} />
                </div>
              </div>
              {noiseBaseline > 15 && (
                <div className="flex items-center gap-2 text-xs text-amber-500">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Background noise detected. Move to a quieter area for better results.
                </div>
              )}
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">What happens:</p>
              <div className="grid gap-2">
                {[
                  { icon: Volume2, text: 'Read 3 short phrases aloud' },
                  { icon: Globe, text: 'System finds your best accent match' },
                  { icon: Zap, text: 'Voice profile optimized for interview' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                    <item.icon className="h-4 w-4 text-indigo-400 shrink-0" />
                    {item.text}
                  </div>
                ))}
              </div>
            </div>

            <Button
              className="w-full"
              onClick={() => setPhase('recording')}
              data-testid="start-calibration-btn"
            >
              <Mic className="h-4 w-4 mr-2" />
              Begin Voice Calibration
            </Button>

            <button
              onClick={() => onComplete({ best_language: 'en-US', clarity_score: 0, speech_rate_wpm: 0 })}
              className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
              data-testid="skip-calibration-btn"
            >
              Skip calibration (not recommended)
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Recording phrases
  if (phase === 'recording') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="voice-calibration-recording">
        <Card className="max-w-lg w-full">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Phrase {currentPhraseIdx + 1} of {CALIBRATION_PHRASES.length}
              </CardTitle>
              <Badge variant="secondary" className="text-xs">{currentPhrase?.label}</Badge>
            </div>
            <Progress value={((currentPhraseIdx + phraseResults.length) / (CALIBRATION_PHRASES.length * 2)) * 100} className="h-1.5 mt-2" />
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Phrase to read */}
            <div className="p-5 rounded-xl border-2 border-dashed border-indigo-500/30 bg-indigo-500/5 text-center">
              <p className="text-xs text-indigo-500 font-medium uppercase tracking-wider mb-2">Read this aloud:</p>
              <p className="text-base font-medium leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                "{currentPhrase?.text}"
              </p>
            </div>

            {/* Audio meter */}
            <div className="flex items-center justify-center">
              <AudioMeter level={audioLevel} active={isListening} />
            </div>

            {/* Live transcript */}
            {liveTranscript && (
              <div className="p-3 rounded-lg bg-muted/40 border min-h-[48px]">
                <p className="text-xs text-muted-foreground mb-1">What we heard:</p>
                <p className="text-sm">{liveTranscript}</p>
              </div>
            )}

            {/* Previous results */}
            {phraseResults.length > 0 && (
              <div className="flex gap-2">
                {phraseResults.map((r, i) => (
                  <Badge
                    key={i}
                    variant={r.accuracy >= 60 ? 'default' : 'secondary'}
                    className="text-xs"
                  >
                    {CALIBRATION_PHRASES[i]?.label}: {r.accuracy}%
                  </Badge>
                ))}
              </div>
            )}

            {/* Record button */}
            {!isListening ? (
              <Button
                className="w-full"
                onClick={startPhraseRecording}
                data-testid="record-phrase-btn"
              >
                <Mic className="h-4 w-4 mr-2" />
                Tap & Start Speaking
              </Button>
            ) : (
              <Button
                variant="destructive"
                className="w-full"
                onClick={stopRecording}
                data-testid="stop-recording-btn"
              >
                <div className="w-3 h-3 bg-white rounded-full mr-2 animate-pulse" />
                Done Speaking — Tap to Continue
              </Button>
            )}

            <p className="text-xs text-center text-muted-foreground">
              {isListening ? 'Auto-advances after 3s of silence or tap the button above' : 'Click the button and read the phrase above clearly'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Testing accents
  if (phase === 'testing') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="voice-calibration-testing">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-3">
              <Globe className="h-7 w-7 text-indigo-500 animate-spin" style={{ animationDuration: '3s' }} />
            </div>
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Analyzing Your Voice Profile
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Testing accent profiles to find the best match for your speech patterns...
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              {ACCENT_PROFILES.map((profile) => {
                const score = accentResults[profile.code];
                const tested = score !== undefined;
                return (
                  <div key={profile.code} className="flex items-center justify-between p-2 rounded-lg border bg-muted/20">
                    <span className="text-sm">{profile.label}</span>
                    {tested ? (
                      <Badge variant={score > 50 ? 'default' : 'secondary'} className="text-xs">
                        {score}%
                      </Badge>
                    ) : (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Results
  if (phase === 'results') {
    const bestProfile = ACCENT_PROFILES.find(p => p.code === bestLang);
    const clarityLabel = clarityScore >= 80 ? 'Excellent' : clarityScore >= 60 ? 'Good' : clarityScore >= 40 ? 'Fair' : 'Needs Improvement';
    const clarityColor = clarityScore >= 80 ? 'text-green-500' : clarityScore >= 60 ? 'text-blue-500' : clarityScore >= 40 ? 'text-amber-500' : 'text-red-500';

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="voice-calibration-results">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center pb-3">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-green-500/10 flex items-center justify-center mb-3">
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
            <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Voice Profile Ready
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Your microphone is calibrated for optimal voice capture
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Profile summary */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg border bg-muted/30 text-center">
                <Globe className="h-5 w-5 mx-auto mb-1 text-indigo-500" />
                <p className="text-xs text-muted-foreground">Best Match</p>
                <p className="text-sm font-semibold">{bestProfile?.label || bestLang}</p>
              </div>
              <div className="p-3 rounded-lg border bg-muted/30 text-center">
                <Zap className="h-5 w-5 mx-auto mb-1 text-indigo-500" />
                <p className="text-xs text-muted-foreground">Clarity Score</p>
                <p className={`text-sm font-semibold ${clarityColor}`}>{clarityScore}% {clarityLabel}</p>
              </div>
              <div className="p-3 rounded-lg border bg-muted/30 text-center">
                <Volume2 className="h-5 w-5 mx-auto mb-1 text-indigo-500" />
                <p className="text-xs text-muted-foreground">Speech Rate</p>
                <p className="text-sm font-semibold">{speechRate} WPM</p>
              </div>
              <div className="p-3 rounded-lg border bg-muted/30 text-center">
                <Shield className="h-5 w-5 mx-auto mb-1 text-indigo-500" />
                <p className="text-xs text-muted-foreground">Noise Level</p>
                <p className={`text-sm font-semibold ${noiseBaseline > 15 ? 'text-amber-500' : 'text-green-500'}`}>
                  {noiseBaseline > 15 ? 'Moderate' : 'Low'}
                </p>
              </div>
            </div>

            {/* Phrase accuracy breakdown */}
            {phraseResults.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Phrase Accuracy</p>
                {phraseResults.map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-16 shrink-0">{CALIBRATION_PHRASES[i]?.label}</span>
                    <Progress value={r.accuracy} className="h-2 flex-1" />
                    <span className="text-xs font-medium w-10 text-right">{r.accuracy}%</span>
                  </div>
                ))}
              </div>
            )}

            {clarityScore < 40 && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  Voice clarity is low. Speak slowly and clearly during the interview.
                  A text input fallback will be available if voice capture struggles.
                </p>
              </div>
            )}

            <Button
              className="w-full"
              onClick={handleSaveAndProceed}
              disabled={saving}
              data-testid="proceed-to-interview-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ChevronRight className="h-4 w-4 mr-2" />}
              Continue to Interview
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
